import { useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomTextArea,
} from '../../../common/components';
import { useDeliverConsumption } from '../../../hooks/useConsumptions';
import {
  buildDeliverPayload,
  getConsumptionApiErrorMessage,
  getInitialDeliverState,
  validateDeliverConsumptionForm,
} from '../utils/consumptionsUtils';

const DeliverConsumptionDialog = ({ open, onClose, consumption, onSuccess }) => {
  const [formData, setFormData] = useState(getInitialDeliverState);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const deliverMutation = useDeliverConsumption();

  useEffect(() => {
    if (open) {
      setFormData(getInitialDeliverState());
      setErrors({});
      setErrorMessage('');
    }
  }, [open, consumption?.uuid]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateDeliverConsumptionForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!consumption?.uuid) return;

    try {
      setErrorMessage('');
      const payload = buildDeliverPayload(formData);
      const updatedConsumption = await deliverMutation.mutateAsync({
        id: consumption.uuid,
        payload,
      });
      onSuccess?.(updatedConsumption);
      onClose();
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo registrar la entrega')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer open={open} onClose={onClose} size="medium">
      <DialogTitle>Registrar entrega</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <CustomTextField
          label="Consumo"
          name="consumptionNumber"
          value={consumption?.consumption_number || '-'}
          disabled
        />

        <CustomTextField
          label="Responsable"
          name="delivery_responsible"
          value={formData.delivery_responsible}
          onChange={handleChange}
          required
          error={Boolean(errors.delivery_responsible)}
          helperText={errors.delivery_responsible}
        />

        <CustomTextField
          label="Dirección de entrega"
          name="delivery_address"
          value={formData.delivery_address}
          onChange={handleChange}
          required
          error={Boolean(errors.delivery_address)}
          helperText={errors.delivery_address}
        />

        <CustomTextField
          label="Fecha de entrega"
          name="delivery_date"
          type="date"
          value={formData.delivery_date}
          onChange={handleChange}
          required
          error={Boolean(errors.delivery_date)}
          helperText={errors.delivery_date}
          InputLabelProps={{ shrink: true }}
        />

        <CustomTextField
          label="Hora de entrega"
          name="delivery_time"
          type="time"
          value={formData.delivery_time}
          onChange={handleChange}
          required
          error={Boolean(errors.delivery_time)}
          helperText={errors.delivery_time}
          InputLabelProps={{ shrink: true }}
        />

        <CustomTextArea
          label="Observaciones"
          name="observations"
          value={formData.observations}
          onChange={handleChange}
        />
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel="Registrar entrega"
        saveButtonProps={{ isLoading: deliverMutation.isPending }}
      />
    </DialogContainer>
  );
};

export default DeliverConsumptionDialog;
